/**
 * GARCHValidationPanel - GARCH Model Validation and Diagnostic Component
 *
 * This component provides comprehensive validation and diagnostic information
 * for GARCH model implementation, helping users understand model performance
 * and ensuring correct functionality.
 *
 * Key Features:
 * - GARCH model parameter validation
 * - Historical data quality checks
 * - Model convergence diagnostics
 * - Performance benchmarking
 * - Interactive parameter exploration
 * - Validation test suite results
 *
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 * @since 2.1.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextContent,
} from '@cloudscape-design/components';
import { GARCHRateCalculationIntegration } from '../../utils/garchModeling';

/**
 * Props für die GARCHValidationPanel-Komponente
 */
interface GARCHValidationPanelProps {
  /** Optional: trigger validation refresh */
  onRefreshValidation?: () => void;
}

interface ValidationResult {
  test: string;
  status: 'passed' | 'warning' | 'failed' | 'info';
  message: string;
  details?: string;
  value?: number;
  expectedRange?: [number, number];
}

/**
 * GARCHValidationPanel - Main validation component
 */
export const GARCHValidationPanel: React.FC<GARCHValidationPanelProps> = ({
  onRefreshValidation,
}) => {
  const [isRunningValidation, setIsRunningValidation] = useState(false);
  const [showDetailedResults, setShowDetailedResults] = useState(false);

  // Run comprehensive GARCH validation tests
  const validationResults = useMemo(() => {
    const results: ValidationResult[] = [];

    try {
      // Test 1: GARCH Model Parameter Validation
      const garchAnalysis = GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');

      // Volatility Persistence Test (should be between 0.7 and 0.99)
      const volPersistence = parseFloat(garchAnalysis.garchParameters.volatilityPersistence);
      results.push({
        test: 'Volatilitäts-Persistenz Parameter',
        status: volPersistence >= 0.7 && volPersistence <= 0.99 ? 'passed' : 'warning',
        message:
          volPersistence >= 0.7 && volPersistence <= 0.99
            ? 'Parameter im erwarteten Bereich'
            : 'Parameter außerhalb typischer Werte',
        details: `β = ${volPersistence.toFixed(4)} (Erwartet: 0.70-0.99)`,
        value: volPersistence,
        expectedRange: [0.7, 0.99],
      });

      // ARCH Effect Test (should be between 0.05 and 0.3)
      const archEffect = parseFloat(garchAnalysis.garchParameters.archEffect);
      results.push({
        test: 'ARCH-Effekt Parameter',
        status: archEffect >= 0.05 && archEffect <= 0.3 ? 'passed' : 'warning',
        message:
          archEffect >= 0.05 && archEffect <= 0.3
            ? 'ARCH-Effekt in normalem Bereich'
            : 'ARCH-Effekt ungewöhnlich',
        details: `α = ${archEffect.toFixed(4)} (Erwartet: 0.05-0.30)`,
        value: archEffect,
        expectedRange: [0.05, 0.3],
      });

      // Leverage Effect Test: θ (signierter Term) should be negative, between -0.3 and 0
      // LABEL-FIX: leverageEffect trägt jetzt korrekt θ (vorher fälschlich γ)
      const leverageEffect = parseFloat(garchAnalysis.garchParameters.leverageEffect);
      results.push({
        test: 'Leverage-Effekt Parameter',
        status: leverageEffect >= -0.3 && leverageEffect <= 0 ? 'passed' : 'warning',
        message:
          leverageEffect >= -0.3 && leverageEffect <= 0
            ? 'Leverage-Effekt zeigt erwartete Asymmetrie'
            : 'Leverage-Effekt ungewöhnlich',
        details: `θ = ${leverageEffect.toFixed(4)} (Erwartet: -0.30 bis 0.00)`,
        value: leverageEffect,
        expectedRange: [-0.3, 0],
      });

      // Clustering Effect Test: γ (Magnituden-Term) muss POSITIV sein,
      // damit große Schocks die Folge-Volatilität erhöhen (Volatilitäts-Clustering)
      const clusteringEffect = parseFloat(garchAnalysis.garchParameters.clusteringEffect);
      results.push({
        test: 'Clustering-Effekt Parameter',
        status: clusteringEffect >= 0.05 && clusteringEffect <= 0.4 ? 'passed' : 'warning',
        message:
          clusteringEffect >= 0.05 && clusteringEffect <= 0.4
            ? 'Volatilitäts-Clustering vorhanden (γ > 0)'
            : 'Clustering-Effekt ungewöhnlich',
        details: `γ = ${clusteringEffect.toFixed(4)} (Erwartet: 0.05 bis 0.40)`,
        value: clusteringEffect,
        expectedRange: [0.05, 0.4],
      });

      // Model Stability Test (sum of persistence parameters)
      const stability = volPersistence + archEffect;
      results.push({
        test: 'Modell-Stabilität',
        status: stability < 1.0 ? 'passed' : 'failed',
        message: stability < 1.0 ? 'Modell ist stabil (stationär)' : 'Modell könnte instabil sein',
        details: `β + α = ${stability.toFixed(4)} (Muss < 1.0 sein)`,
        value: stability,
        expectedRange: [0, 1],
      });

      // Data Quality Tests
      results.push({
        test: 'Historische Daten Verfügbarkeit',
        status: garchAnalysis.dataPoints >= 1000 ? 'passed' : 'warning',
        message:
          garchAnalysis.dataPoints >= 1000
            ? 'Ausreichend historische Daten'
            : 'Begrenzte Datenbasis',
        details: `${garchAnalysis.dataPoints} Datenpunkte verfügbar`,
        value: garchAnalysis.dataPoints,
        expectedRange: [1000, 10000],
      });

      // Historical Statistics Validation
      const historicalVol = parseFloat(garchAnalysis.historicalVolatility);
      results.push({
        test: 'Historische Volatilität',
        status: historicalVol >= 10 && historicalVol <= 30 ? 'passed' : 'warning',
        message:
          historicalVol >= 10 && historicalVol <= 30
            ? 'Volatilität in erwartetem Bereich'
            : 'Ungewöhnliche Volatilität',
        details: `${historicalVol}% annualisiert (Typisch: 10-30%)`,
        value: historicalVol,
        expectedRange: [10, 30],
      });

      // Skewness Test (should be negative for financial returns)
      const skewness = parseFloat(garchAnalysis.skewness);
      results.push({
        test: 'Rendite-Verteilung Schiefe',
        status: skewness < 0 ? 'passed' : 'warning',
        message: skewness < 0 ? 'Negative Schiefe wie erwartet' : 'Positive Schiefe ungewöhnlich',
        details: `Schiefe = ${skewness.toFixed(3)} (Erwartet: < 0)`,
        value: skewness,
        expectedRange: [-2, 0],
      });

      // Kurtosis Test (informational - diversified indices can have lower kurtosis)
      const kurtosis = parseFloat(garchAnalysis.kurtosis);
      const getKurtosisStatus = (k: number) => {
        if (k > 3) {return 'passed';}
        if (k > -1) {return 'info';} // Informational for normal range
        return 'warning'; // Only warn for extremely low values
      };
      const getKurtosisMessage = (k: number) => {
        if (k > 3) {return 'Fat Tails vorhanden (typisch für Finanzrenditen)';}
        if (k > 0) {return 'Moderate Kurtosis (normal für diversifizierte Indizes)';}
        if (k > -1) {return 'Leichte Tails (normal für MSCI World/ACWI Daten)';}
        return 'Sehr niedrige Kurtosis - Datenqualität prüfen';
      };
      const getKurtosisDetails = (k: number) => {
        if (k > 3) {return `Kurtosis = ${k.toFixed(3)} (Fat Tails bestätigt)`;}
        if (k > -1) {return `Kurtosis = ${k.toFixed(3)} (Acceptable für Index-Daten)`;}
        return `Kurtosis = ${k.toFixed(3)} (Ungewöhnlich niedrig)`;
      };
      
      results.push({
        test: 'Kurtosis (Tail-Verhalten)',
        status: getKurtosisStatus(kurtosis),
        message: getKurtosisMessage(kurtosis),
        details: getKurtosisDetails(kurtosis),
        value: kurtosis,
        expectedRange: [-1, 10],
      });
    } catch (error) {
      results.push({
        test: 'GARCH Model Loading',
        status: 'failed',
        message: 'Fehler beim Laden des GARCH Modells',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }

    // Test GARCH Rate Generation
    try {
      const testRates = GARCHRateCalculationIntegration.generateSingleGARCHRate(
        'historische-garch',
        'world'
      );
      results.push({
        test: 'GARCH Rate Generation',
        status: typeof testRates === 'number' && !isNaN(testRates) ? 'passed' : 'failed',
        message:
          typeof testRates === 'number' && !isNaN(testRates)
            ? 'Rate-Generierung funktioniert'
            : 'Rate-Generierung fehlgeschlagen',
        details: `Test-Rate: ${typeof testRates === 'number' ? testRates.toFixed(2) : 'N/A'}%`,
        value: typeof testRates === 'number' ? testRates : undefined,
      });
    } catch (error) {
      results.push({
        test: 'GARCH Rate Generation',
        status: 'failed',
        message: 'Rate-Generierung fehlgeschlagen',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }

    // Test GARCH Rate Sequence Generation
    try {
      const testSequence = GARCHRateCalculationIntegration.generateInvestitionstopfReturns(
        'historische-garch',
        10,
        'world'
      );

      results.push({
        test: 'GARCH Sequence Generation',
        status: Array.isArray(testSequence) && testSequence.length === 10 ? 'passed' : 'failed',
        message:
          Array.isArray(testSequence) && testSequence.length === 10
            ? 'Sequenz-Generierung funktioniert'
            : 'Sequenz-Generierung fehlgeschlagen',
        details: `${Array.isArray(testSequence) ? testSequence.length : 0} Rates generiert (Erwartet: 10)`,
        value: Array.isArray(testSequence) ? testSequence.length : 0,
      });
    } catch (error) {
      results.push({
        test: 'GARCH Sequence Generation',
        status: 'failed',
        message: 'Sequenz-Generierung fehlgeschlagen',
        details: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }

    return results;
  }, []);

  // Calculate validation summary
  const validationSummary = useMemo(() => {
    const passed = validationResults.filter(r => r.status === 'passed').length;
    const warnings = validationResults.filter(r => r.status === 'warning').length;
    const failed = validationResults.filter(r => r.status === 'failed').length;
    const info = validationResults.filter(r => r.status === 'info').length;
    const total = validationResults.length;

    return {
      passed,
      warnings,
      failed,
      info,
      total,
      overallStatus: failed > 0 ? 'failed' : warnings > 0 ? 'warning' : 'passed',
      successRate: ((passed + info) / total) * 100, // Include info as successful
    };
  }, [validationResults]);

  // Validation table columns
  const validationTableColumns = [
    {
      id: 'test',
      header: 'Test',
      cell: (item: ValidationResult) => item.test,
      width: 200,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: ValidationResult) => (
        <StatusIndicator
          type={
            item.status === 'passed' 
              ? 'success' 
              : item.status === 'warning' 
                ? 'warning' 
                : item.status === 'info'
                  ? 'info'
                  : 'error'
          }
        >
          {item.status === 'passed'
            ? 'Bestanden'
            : item.status === 'warning'
              ? 'Warnung'
              : item.status === 'info'
                ? 'Info'
                : 'Fehler'}
        </StatusIndicator>
      ),
      width: 100,
    },
    {
      id: 'message',
      header: 'Ergebnis',
      cell: (item: ValidationResult) => item.message,
      width: 250,
    },
    {
      id: 'details',
      header: 'Details',
      cell: (item: ValidationResult) => (
        <Box fontSize="body-s" color="text-body-secondary">
          {item.details || 'N/A'}
        </Box>
      ),
      width: 300,
    },
  ];

  // Run comprehensive validation
  const handleRunValidation = async () => {
    setIsRunningValidation(true);

    // Simulate validation process
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsRunningValidation(false);
    onRefreshValidation?.();
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Header */}
      <Container>
        <Header variant="h2">GARCH Model Validation & Diagnostics</Header>
        <SpaceBetween direction="vertical" size="m">
          <Alert
            type={
              validationSummary.overallStatus === 'passed'
                ? 'success'
                : validationSummary.overallStatus === 'warning'
                  ? 'warning'
                  : 'error'
            }
          >
            <strong>Validation Status:</strong> {validationSummary.passed}/{validationSummary.total}{' '}
            Tests bestanden ({validationSummary.successRate.toFixed(1)}% Erfolgsrate)
            {validationSummary.info > 0 && `, ${validationSummary.info} Informationen`}
            {validationSummary.warnings > 0 && `, ${validationSummary.warnings} Warnungen`}
            {validationSummary.failed > 0 && `, ${validationSummary.failed} Fehler`}
          </Alert>

          <TextContent>
            <p>
              Diese Validierung überprüft die Korrektheit und Stabilität der
              GARCH-Modell-Implementierung. Alle Tests sollten bestanden werden, um optimale
              Volatilitäts-Modellierung zu gewährleisten.
            </p>
          </TextContent>
        </SpaceBetween>
      </Container>

      {/* Validation Summary */}
      <Container header={<Header variant="h3">Validation Summary</Header>}>
        <ColumnLayout columns={5} borders="horizontal">
          <Box>
            <Box variant="awsui-key-label">Tests Bestanden</Box>
            <Box variant="awsui-value-large" color="text-status-success">
              {validationSummary.passed}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Informationen</Box>
            <Box variant="awsui-value-large" color="text-status-info">
              {validationSummary.info}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Warnungen</Box>
            <Box variant="awsui-value-large" color="text-status-warning">
              {validationSummary.warnings}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Fehler</Box>
            <Box variant="awsui-value-large" color="text-status-error">
              {validationSummary.failed}
            </Box>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Erfolgsrate</Box>
            <ProgressBar
              value={validationSummary.successRate}
              label={`${validationSummary.successRate.toFixed(1)}%`}
              status={
                validationSummary.successRate >= 90
                  ? 'success'
                  : validationSummary.successRate >= 70
                    ? 'in-progress'
                    : 'error'
              }
            />
          </Box>
        </ColumnLayout>
      </Container>

      {/* Validation Controls */}
      <Container header={<Header variant="h3">Validation Controls</Header>}>
        <SpaceBetween direction="horizontal" size="m">
          <Button variant="primary" onClick={handleRunValidation} loading={isRunningValidation}>
            Validation neu ausführen
          </Button>
          <Button variant="normal" onClick={() => setShowDetailedResults(!showDetailedResults)}>
            {showDetailedResults ? 'Details verbergen' : 'Details anzeigen'}
          </Button>
        </SpaceBetween>
      </Container>

      {/* Validation Results Table */}
      <Container header={<Header variant="h3">Validation Results</Header>}>
        <Table
          columnDefinitions={validationTableColumns}
          items={validationResults}
          loadingText="Führe Validation durch..."
          empty={
            <Box textAlign="center" color="inherit">
              <p>Keine Validation-Ergebnisse verfügbar</p>
            </Box>
          }
        />
      </Container>

      {/* Detailed Analysis */}
      {showDetailedResults && (
        <Container header={<Header variant="h3">Detaillierte GARCH Model Analyse</Header>}>
          <SpaceBetween direction="vertical" size="m">
            <Alert type="info">
              <strong>Model Parameter Interpretation:</strong> GARCH Parameter sollten bestimmte
              Eigenschaften aufweisen um realistische Volatilitäts-Dynamiken zu modellieren.
            </Alert>

            <ColumnLayout columns={2} borders="vertical">
              <Box>
                <Header variant="h3">Parameter-Bereiche</Header>
                <SpaceBetween direction="vertical" size="xs">
                  <p>
                    <strong>Volatilitäts-Persistenz (β):</strong> 0.7 - 0.99
                  </p>
                  <ul>
                    <li>Hohe Werte: Lange Volatilitäts-Gedächtnis</li>
                    <li>Niedrige Werte: Schnelle Volatilitäts-Anpassung</li>
                  </ul>

                  <p>
                    <strong>ARCH-Effekt (α):</strong> 0.05 - 0.30
                  </p>
                  <ul>
                    <li>Stärke des Volatilitäts-Clustering</li>
                    <li>Höhere Werte: Stärkere Clustering-Effekte</li>
                  </ul>

                  <p>
                    <strong>Clustering-Effekt (γ):</strong> 0.05 - 0.40
                  </p>
                  <ul>
                    <li>Magnituden-Effekt: große Schocks erhöhen Folge-Volatilität</li>
                    <li>Positive Werte: Volatilitäts-Clustering (Krisen clustern)</li>
                  </ul>

                  <p>
                    <strong>Leverage-Effekt (θ):</strong> -0.30 - 0.00
                  </p>
                  <ul>
                    <li>Asymmetrische Volatilitäts-Reaktion</li>
                    <li>Negative Werte: Negative Schocks erhöhen Volatilität stärker</li>
                  </ul>
                </SpaceBetween>
              </Box>

              <Box>
                <Header variant="h3">Quality Indicators</Header>
                <SpaceBetween direction="vertical" size="xs">
                  <p>
                    <strong>Model Stabilität:</strong> β + α {'<'} 1.0
                  </p>
                  <ul>
                    <li>Gewährleistet Stationarität</li>
                    <li>Verhindert explosive Volatilität</li>
                  </ul>

                  <p>
                    <strong>Daten-Qualität:</strong> {'>'} 1000 Punkte
                  </p>
                  <ul>
                    <li>Ausreichend für robuste Kalibrierung</li>
                    <li>Mehr Daten verbessern Parameter-Schätzung</li>
                  </ul>

                  <p>
                    <strong>Distribution Properties:</strong>
                  </p>
                  <ul>
                    <li>Negative Skewness (typisch für Aktien)</li>
                    <li>Kurtosis {'>'} 3 (Fat Tails)</li>
                  </ul>
                </SpaceBetween>
              </Box>
            </ColumnLayout>

            {validationSummary.failed > 0 && (
              <Alert type="error">
                <strong>Validation Fehler gefunden:</strong> Einige Tests sind fehlgeschlagen. Bitte
                überprüfen Sie die GARCH-Implementierung oder die historischen Daten.
              </Alert>
            )}

            {validationSummary.warnings > 0 && validationSummary.failed === 0 && (
              <Alert type="warning">
                <strong>Validation Warnungen:</strong> Einige Parameter sind außerhalb der typischen
                Bereiche, aber das Modell funktioniert grundsätzlich.
              </Alert>
            )}

            {validationSummary.overallStatus === 'passed' && (
              <Alert type="success">
                <strong>Alle Validations bestanden:</strong> Das GARCH-Modell ist korrekt kalibriert
                und bereit für realistische Volatilitäts-Simulation.
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};
